import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { supabaseClient } from '@auth-v2';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { toast } from 'sonner';

import { Layout } from '@data';
import { useCreateNewLayout } from '@hooks';
import { useTranslate } from '@i18n';
import {
    queryClient,
    queryKeys,
    useClubCollections,
    useDeleteLayout,
    usePublicLayouts,
    useUpdateActivity,
    useUserLayouts,
} from '@queries';
import { activeUserIdAtom, activityAtom } from '@store';
import {
    ActionButton,
    Box,
    Button,
    colors,
    ElvaLogoSimple,
    Heading3,
    Inline,
    ModalBase,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Stack,
    StatusBox,
    Tabs,
    Text,
    u,
} from '@ui';

import { DeleteLayoutModal } from '../../DeleteLayoutModal/DeleteLayoutModal';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';

import { FooterSecondaryControls } from './FooterSecondaryControls';
import { LayoutNameInput } from './LayoutNameInput';
import * as css from './LayoutPresetModal.css';
import { NewLayoutForm } from './NewLayoutForm';
import { Select } from './Select';
import { useCreateLayout } from './useCreateLayout';

type LayoutPresetModalProps = {
    activeUserId?: string;
    onEdit?: (layoutId: number) => void;
    onDismiss: () => void;
};

const LAYOUT_TABS = {
    USER: 'user_layouts',
    ELVA: 'elva_layouts',
    CLUB_GROUP: 'club_group_layouts',
};

type UserLayoutsTabProps = {
    selectedLayoutId?: number;
    setActiveLayout: (layout: Layout) => void;
    selectNewLayout?: (layout: Layout) => void;
    handleEdit: (layoutId: number) => void;
};

const UserLayoutsTab = ({ selectedLayoutId, setActiveLayout, handleEdit }: UserLayoutsTabProps) => {
    const { data: layouts, isLoading } = useUserLayouts();
    const [editLayoutId, setEditLayoutId] = useState<number | null>(null);
    const activity = useAtomValue(activityAtom);
    const clubGroupId = activity?.club?.clubTypeId;
    const clubLayouts = useMemo(() => {
        const clubLayoutGroups: Record<'unassigned' | 'otherClub', Layout[]> = {
            unassigned: [],
            otherClub: [],
        };
        return (
            layouts?.reduce((acc, layout) => {
                if (!layout.clubTypeId) acc.unassigned.push(layout);
                else if (layout.clubTypeId !== clubGroupId) acc.otherClub.push(layout);
                return acc;
            }, clubLayoutGroups) || clubLayoutGroups
        );
    }, [clubGroupId, layouts]);
    const [reassignLayout, setReassignLayout] = useState<Layout | null>(null);

    const { mutateAsync: createNewLayout } = useCreateNewLayout();
    const handleSelectCopy = useCallback(
        async (layout: Layout) => {
            const layoutCopy = await createNewLayout({
                name: layout.name,
                sourceLayoutId: layout.id,
                clubTypeId: clubGroupId ?? undefined,
            });

            if (!layoutCopy) {
                throw new Error('Failed to create a copy of the selected layout');
            }
            setActiveLayout(layoutCopy);
        },
        [clubGroupId, createNewLayout, setActiveLayout],
    );

    return (
        <>
            <LayoutListContainer
                isLoading={isLoading}
                isEmpty={layouts?.length === 0}
                emptyMessage="User has no Layouts"
            >
                {!!clubLayouts.unassigned.length && <Heading3>Unassigned Layouts</Heading3>}
                {clubLayouts.unassigned?.map((layout) => (
                    <UserLayoutSelect
                        layout={layout}
                        setActiveLayout={setActiveLayout}
                        handleEdit={handleEdit}
                        selectedLayoutId={selectedLayoutId}
                        isEditing={editLayoutId === layout.id}
                        toggleEdit={(id) => setEditLayoutId(id)}
                        key={layout.id}
                    />
                ))}
                {!!clubLayouts.otherClub.length && <Heading3>Assigned to Other Clubs</Heading3>}
                {clubLayouts.otherClub?.map((layout) => (
                    <UserLayoutSelect
                        layout={layout}
                        setActiveLayout={setReassignLayout}
                        handleEdit={handleEdit}
                        selectedLayoutId={selectedLayoutId}
                        isEditing={editLayoutId === layout.id}
                        toggleEdit={(id) => setEditLayoutId(id)}
                        selectNewLayout={handleSelectCopy}
                        key={layout.id}
                    />
                ))}
            </LayoutListContainer>
            {reassignLayout && (
                <ConfirmModal
                    title={`Reassign '${reassignLayout?.name}'?`}
                    message={`
                        Would you like to reassign '${reassignLayout?.name}' to current club group?
                        This will remove it from its current club group and assign it to the new one.
                    `}
                    onAccept={() => {
                        setActiveLayout(reassignLayout);
                        setReassignLayout(null);
                    }}
                    onCancel={() => setReassignLayout(null)}
                />
            )}
        </>
    );
};

const ClubLayoutsTab = ({ selectedLayoutId, setActiveLayout, handleEdit }: UserLayoutsTabProps) => {
    const { data: layouts, isLoading } = useUserLayouts();
    const [editLayoutId, setEditLayoutId] = useState<number | null>(null);
    const activity = useAtomValue(activityAtom);

    const filteredLayouts = useMemo(() => {
        return layouts?.filter((layout) => layout.clubTypeId === activity?.club?.clubTypeId);
    }, [layouts, activity?.club?.clubTypeId]);

    return (
        <LayoutListContainer
            isLoading={isLoading}
            isEmpty={filteredLayouts?.length === 0}
            emptyMessage="User has no Layouts"
        >
            {filteredLayouts?.map((layout) => (
                <ClubLayoutSelect
                    layout={layout}
                    setActiveLayout={setActiveLayout}
                    handleEdit={handleEdit}
                    selectedLayoutId={selectedLayoutId}
                    isEditing={editLayoutId === layout.id}
                    toggleEdit={(id) => setEditLayoutId(id)}
                    key={layout.id}
                />
            ))}
        </LayoutListContainer>
    );
};

type ElvaLayoutsTabProps = Pick<UserLayoutsTabProps, 'selectedLayoutId' | 'setActiveLayout'>;

const ElvaLayoutsTab = ({ selectedLayoutId, setActiveLayout }: ElvaLayoutsTabProps) => {
    const { data: layouts, isLoading } = usePublicLayouts();

    return (
        <LayoutListContainer isLoading={isLoading} isEmpty={layouts?.length === 0} emptyMessage="ELVA has no layouts">
            {layouts?.map((layout) => (
                <ElvaLayoutSelect
                    layout={layout}
                    setActiveLayout={setActiveLayout}
                    selectedLayoutId={selectedLayoutId}
                    key={layout.id}
                />
            ))}
        </LayoutListContainer>
    );
};

const useUpdateLayoutClubType = () => {
    const activeUserId = useAtomValue(activeUserIdAtom);

    return useMutation({
        mutationFn: async ({ layoutId, clubTypeId }: { layoutId: number; clubTypeId: number | null }) => {
            const { error } = await supabaseClient
                .from('layouts')
                .update({ club_type_id: clubTypeId })
                .eq('id', layoutId);
            if (error) {
                throw error;
            }
        },
        onSuccess: () => {
            toast.success('Layout updated successfully');
            if (!activeUserId) return;
            queryClient.invalidateQueries({
                predicate: (query) =>
                    queryKeys.layout.base(activeUserId).every((key, index) => query.queryKey[index] === key),
            });
        },
    });
};

export const LayoutPresetModal = ({ onDismiss, onEdit }: LayoutPresetModalProps) => {
    const activity = useAtomValue(activityAtom);
    const activeLayoutId = activity?.layoutId ?? -1;
    const activityClubType = activity?.club?.clubTypeId;
    const activityClubTypeName = activity?.club?.typeName;
    const { mutateAsync: updateActivity } = useUpdateActivity();
    const { mutateAsync: updateLayoutClubType } = useUpdateLayoutClubType();

    const tabItems = useMemo(
        () => [
            { id: LAYOUT_TABS.CLUB_GROUP, label: `${activityClubTypeName}` },
            { id: LAYOUT_TABS.ELVA, label: 'ELVA Layouts' },
            { id: LAYOUT_TABS.USER, label: 'My Layouts (OLD)' },
        ],
        [activityClubTypeName],
    );

    const setActiveLayout = useCallback(
        async (layout?: Layout) => {
            if (!activity) {
                throw new Error('No activity found');
            }
            await updateActivity({
                layoutId: layout?.id,
                activityId: activity?.id,
            });

            if (layout?.id && activityClubType !== layout.clubTypeId) {
                updateLayoutClubType({
                    layoutId: layout.id,
                    clubTypeId: activityClubType ?? null,
                });
            }
        },
        [activity, activityClubType, updateActivity, updateLayoutClubType],
    );

    const { isAddingLayout, startAddingLayout, cancelAddingLayout, saveLayout, changeCreateMode, isLayoutSavePending } =
        useCreateLayout(setActiveLayout, activeLayoutId);

    const handleEdit = useCallback(
        (layoutId: number) => {
            onEdit?.(layoutId);
            onDismiss();
        },
        [onDismiss, onEdit],
    );

    const [activeTab, setActiveTab] = useState(LAYOUT_TABS.CLUB_GROUP);
    const { data: layouts, isLoading: isLoadingLayouts } = useUserLayouts();
    const hasUserLayouts = !isLoadingLayouts && !!layouts?.length;

    const t = useTranslate();

    const { mutateAsync: createNewLayout } = useCreateNewLayout();

    const selectNew = useCallback(
        async (layout: Layout) => {
            const layoutCopy = await createNewLayout({
                name: layout.name,
                sourceLayoutId: layout.id,
                clubTypeId: activityClubType ?? undefined,
            });

            if (!layoutCopy) {
                throw new Error('Failed to create a copy of the selected layout');
            }
            setActiveLayout(layoutCopy);
            setActiveTab(LAYOUT_TABS.CLUB_GROUP);
        },
        [createNewLayout, activityClubType, setActiveLayout],
    );

    return (
        <ModalBase onDismiss={onDismiss} open variant="partial">
            <ModalHeader>Layout selection</ModalHeader>
            {hasUserLayouts && (
                <ModalBody>
                    <Tabs items={tabItems} activeId={activeTab} onClick={setActiveTab} />
                </ModalBody>
            )}
            <ModalBody py={u(4)} flexShrink={1}>
                {activeTab === LAYOUT_TABS.USER && (
                    <StatusBox
                        title="Warning"
                        status="error"
                        description="'My Layouts (OLD)' will be deprecated. Select a layout within this list to assign it to a specific club group. All remaining unassigned layouts will be deleted."
                    />
                )}
                <div className={css.layoutTab}>
                    {hasUserLayouts && activeTab === LAYOUT_TABS.USER && (
                        <UserLayoutsTab
                            setActiveLayout={(layout) => {
                                setActiveLayout(layout);
                                setActiveTab(LAYOUT_TABS.CLUB_GROUP);
                            }}
                            selectedLayoutId={activeLayoutId}
                            handleEdit={handleEdit}
                        />
                    )}
                    {hasUserLayouts && activeTab === LAYOUT_TABS.CLUB_GROUP && (
                        <ClubLayoutsTab
                            setActiveLayout={setActiveLayout}
                            selectedLayoutId={activeLayoutId}
                            handleEdit={handleEdit}
                        />
                    )}
                    {(!hasUserLayouts || activeTab === LAYOUT_TABS.ELVA) && (
                        <ElvaLayoutsTab setActiveLayout={selectNew} selectedLayoutId={activeLayoutId} />
                    )}
                </div>
            </ModalBody>
            <ModalBody>
                {isAddingLayout && (
                    <NewLayoutForm
                        onCancel={cancelAddingLayout}
                        onSave={saveLayout}
                        isLayoutSavePending={isLayoutSavePending}
                    />
                )}
            </ModalBody>
            <ModalFooter
                primary={
                    <Button variant="secondary" size="medium" onClick={onDismiss}>
                        {t('generic_ui.close')}
                    </Button>
                }
                secondary={
                    <FooterSecondaryControls
                        isAddingLayout={isAddingLayout}
                        changeCreateMode={changeCreateMode}
                        startAddingLayout={startAddingLayout}
                    />
                }
            />
        </ModalBase>
    );
};

type LayoutListContainerProps = PropsWithChildren<{
    isLoading: boolean;
    emptyMessage: string;
    isEmpty: boolean;
}>;

export const LayoutListContainer = ({ isLoading, children, emptyMessage, isEmpty }: LayoutListContainerProps) => {
    return (
        <Stack>
            {isLoading && <Spinner size="m" />}
            {children}
            {!isLoading && isEmpty && (
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Text>{emptyMessage}</Text>
                </Box>
            )}
        </Stack>
    );
};

type LayoutSelectProps = UserLayoutsTabProps & {
    layout: Layout;
};

type UserLayoutSelectProps = LayoutSelectProps & {
    isEditing: boolean;
    toggleEdit: (id: number | null) => void;
    selectNewLayout?: (layout: Layout) => void;
};
const ClubLayoutSelect = ({
    layout,
    isEditing,
    toggleEdit,
    selectedLayoutId,
    setActiveLayout,
    selectNewLayout,
}: UserLayoutSelectProps) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { mutateAsync: deleteLayout } = useDeleteLayout();

    const handleDeleteLayout = useCallback(async () => {
        await deleteLayout(layout.id);
        setShowDeleteModal(false);
    }, [deleteLayout, layout.id]);

    const isActiveLayout = selectedLayoutId === layout.id;

    return (
        <>
            <Select
                value={layout.id}
                selected={selectedLayoutId === layout.id}
                onSelect={() => setActiveLayout(layout)}
                action={
                    <Inline gap={u(1.5)}>
                        {selectNewLayout ? (
                            <ActionButton
                                icon="copy"
                                variant="plain"
                                onClick={() => selectNewLayout(layout)}
                                iconSize="large"
                            />
                        ) : null}
                        {!isActiveLayout ? (
                            <ActionButton
                                icon="trash"
                                variant="plain"
                                onClick={() => setShowDeleteModal(true)}
                                iconColor={colors.red[500]}
                                iconSize="large"
                            />
                        ) : null}
                        <ActionButton
                            icon="pencil-circle"
                            onClick={() => toggleEdit(isEditing ? null : layout.id)}
                            variant="plain"
                            iconSize="large"
                        />
                    </Inline>
                }
            >
                <LayoutNameInput layout={layout} onCancelEdit={() => toggleEdit(null)} isEditing={isEditing} />
            </Select>
            {showDeleteModal ? (
                <DeleteLayoutModal
                    isOpen={showDeleteModal}
                    layoutId={layout.id}
                    onDismiss={() => setShowDeleteModal(false)}
                    onDelete={handleDeleteLayout}
                />
            ) : null}
        </>
    );
};

const UserLayoutSelect = ({
    layout,
    isEditing,
    toggleEdit,
    selectedLayoutId,
    setActiveLayout,
    selectNewLayout,
}: UserLayoutSelectProps) => {
    const t = useTranslate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { mutateAsync: deleteLayout } = useDeleteLayout();

    const handleDeleteLayout = useCallback(async () => {
        await deleteLayout(layout.id);
        setShowDeleteModal(false);
    }, [deleteLayout, layout.id]);

    const isActiveLayout = selectedLayoutId === layout.id;
    const isUnassignedLayout = !layout.clubTypeId;

    const { data: clubCollections } = useClubCollections();
    const clubTypeNameMap = useMemo(
        () =>
            Object.fromEntries(
                clubCollections?.map((collection) => [collection.id, collection.typeName] as const) ?? [],
            ),
        [clubCollections],
    );

    return (
        <>
            <Select
                value={layout.id}
                selected={selectedLayoutId === layout.id}
                onSelect={() => setActiveLayout(layout)}
                info={
                    isUnassignedLayout
                        ? t('activity_wizard.no_club_assigned')
                        : clubTypeNameMap[layout.clubTypeId ?? -1]
                }
                action={
                    <Inline gap={u(1.5)}>
                        {selectNewLayout ? (
                            <ActionButton
                                icon="copy"
                                variant="plain"
                                onClick={() => selectNewLayout(layout)}
                                iconSize="large"
                            />
                        ) : null}
                        {!isActiveLayout ? (
                            <ActionButton
                                icon="trash"
                                variant="plain"
                                onClick={() => setShowDeleteModal(true)}
                                iconColor={colors.red[500]}
                                iconSize="large"
                            />
                        ) : null}
                        <ActionButton
                            icon="pencil-circle"
                            onClick={() => toggleEdit(isEditing ? null : layout.id)}
                            variant="plain"
                            iconSize="large"
                        />
                    </Inline>
                }
            >
                <LayoutNameInput layout={layout} onCancelEdit={() => toggleEdit(null)} isEditing={isEditing} />
            </Select>
            {showDeleteModal ? (
                <DeleteLayoutModal
                    isOpen={showDeleteModal}
                    layoutId={layout.id}
                    onDismiss={() => setShowDeleteModal(false)}
                    onDelete={handleDeleteLayout}
                />
            ) : null}
        </>
    );
};

export const ElvaLayoutSelect = ({
    layout,
    selectedLayoutId,
    setActiveLayout,
}: Omit<LayoutSelectProps, 'handleEdit'>) => {
    const isActiveLayout = selectedLayoutId === layout.id;
    return (
        <Select
            value={layout.id}
            selected={isActiveLayout}
            onSelect={() => setActiveLayout(layout)}
            icon={<ElvaLogoSimple width={32} />}
        >
            {layout.name}
        </Select>
    );
};
